// ============================================================
// AURORA BY NEUROTECH-X — ETAPA 6: PAINEL ADMIN
// ============================================================
// Arquivo: etapa6-admin.jsx
// Rota: /admin (só visível para role === 'admin')
// Visual: tema dark #0F1729 + gradiente gold→orange→red
// ============================================================

import { t, getLang, setLang } from "./i18n";
import React, { useState, useEffect, useCallback } from 'react';

// ============================================================
// CONFIGURAÇÃO & FETCH SERVICE
// ============================================================
const API_BASE = '/api/admin';

const fetchApi = async (endpoint, options = {}) => {
  const token = window.__auroraToken;
  if (!token) {
    window.location.href = '/login';
    return null;
  }
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
    ...options,
  };

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, config);
    if (res.status === 401) {
      
      window.location.href = '/login';
      return null;
    }
    if (res.status === 409) {
      const data = await res.json();
      throw new Error(data.error || 'Conflito');
    }
    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Erro na requisição');
    }
    return await res.json();
  } catch (err) {
    throw err;
  }
};

// ============================================================
// ESTILOS GLOBAIS DO ADMIN
// ============================================================
const colors = {
  bg: '#0F1729',
  bgCard: '#1A2332',
  bgCardHover: '#1E2A3C',
  bgInput: '#0D1320',
  border: '#2A3A4E',
  borderFocus: '#E8972C',
  text: '#E2E8F0',
  textMuted: '#8899AA',
  textDim: '#5A6B7E',
  gold: '#E8972C',
  orange: '#E86A2C',
  red: '#E83A2C',
  green: '#34D399',
  greenDim: '#065F46',
  yellow: '#FBBF24',
  yellowDim: '#78350F',
  blue: '#60A5FA',
  purple: '#A78BFA',
  gradientText: 'linear-gradient(135deg, #E8972C, #E86A2C, #E83A2C)',
};

const styles = {
  // Layout
  container: {
    minHeight: '100vh',
    background: colors.bg,
    color: colors.text,
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
  },
  header: {
    background: 'linear-gradient(135deg, #0F1729 0%, #1A2332 100%)',
    borderBottom: `1px solid ${colors.border}`,
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: 700,
    background: colors.gradientText,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '1px',
  },
  headerBadge: {
    background: 'linear-gradient(135deg, #E8972C, #E86A2C)',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },

  // Navigation tabs
  nav: {
    display: 'flex',
    gap: '4px',
    padding: '16px 32px 0',
    borderBottom: `1px solid ${colors.border}`,
    background: colors.bg,
  },
  navTab: (active) => ({
    padding: '12px 24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: active ? 600 : 400,
    color: active ? colors.gold : colors.textMuted,
    borderBottom: active ? `2px solid ${colors.gold}` : '2px solid transparent',
    background: 'transparent',
    border: 'none',
    transition: 'all 0.2s',
    marginBottom: '-1px',
  }),

  // Content area
  content: {
    padding: '24px 32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },

  // Cards
  card: {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: colors.text,
    marginBottom: '16px',
  },

  // Metric cards (dashboard)
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  metricCard: (accentColor) => ({
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '20px',
    borderLeft: `4px solid ${accentColor}`,
    transition: 'transform 0.2s, border-color 0.2s',
  }),
  metricValue: {
    fontSize: '28px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  metricLabel: {
    fontSize: '13px',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Tables
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${colors.border}`,
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    borderBottom: `1px solid ${colors.border}`,
    color: colors.text,
  },
  trHover: {
    cursor: 'pointer',
    transition: 'background 0.15s',
  },

  // Status badges
  statusBadge: (status) => {
    const map = {
      active: { bg: colors.greenDim, color: colors.green, label: 'Ativo' },
      suspended: { bg: colors.yellowDim, color: colors.yellow, label: 'Suspenso' },
      cancelled: { bg: '#4A1520', color: colors.red, label: 'Cancelado' },
      trial: { bg: '#1E293B', color: colors.blue, label: 'Trial' },
    };
    const s = map[status] || map.active;
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 600,
      background: s.bg,
      color: s.color,
    };
  },

  // Buttons
  btnPrimary: {
    background: 'linear-gradient(135deg, #E8972C, #E86A2C)',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.2s, transform 0.1s',
  },
  btnSecondary: {
    background: 'transparent',
    color: colors.textMuted,
    border: `1px solid ${colors.border}`,
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  btnDanger: {
    background: 'transparent',
    color: colors.red,
    border: `1px solid ${colors.red}33`,
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSmall: {
    padding: '6px 12px',
    fontSize: '12px',
    borderRadius: '6px',
    cursor: 'pointer',
    border: 'none',
    fontWeight: 500,
  },

  // Inputs
  input: {
    width: '100%',
    padding: '10px 14px',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.text,
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: colors.textMuted,
    marginBottom: '6px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    background: colors.bgInput,
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    color: colors.text,
    fontSize: '14px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },

  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: colors.bgCard,
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    padding: '32px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '85vh',
    overflowY: 'auto',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 700,
    marginBottom: '24px',
    background: colors.gradientText,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: `1px solid ${colors.border}`,
  },

  // Search bar
  searchBar: {
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },

  // Toast
  toast: (type) => ({
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    padding: '14px 24px',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 500,
    zIndex: 2000,
    background: type === 'error' ? '#7F1D1D' : '#065F46',
    color: '#fff',
    border: `1px solid ${type === 'error' ? colors.red : colors.green}`,
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    animation: 'fadeIn 0.3s ease',
  }),

  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: colors.textMuted,
  },

  // Loading
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: colors.textMuted,
    fontSize: '14px',
  },
};

// ============================================================
// MÓDULOS DISPONÍVEIS (para checkbox no plano)
// ============================================================
const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'clientes', label: 'Clientes' },
  { key: 'produtos', label: 'Produtos' },
  { key: 'pdv', label: 'PDV' },
  { key: 'pedidos', label: 'Pedidos' },
  { key: 'estoque', label: 'Estoque' },
  { key: 'os', label: 'Ordens de Serviço' },
  { key: 'trocas', label: 'Trocas/Devoluções' },
  { key: 'relatorios', label: 'Relatórios' },
  { key: 'financeiro', label: 'Financeiro' },
  { key: 'whatsapp', label: 'WhatsApp API' },
  { key: 'ia', label: 'Suporte IA' },
];

// ============================================================
// COMPONENTE: Toast
// ============================================================
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div style={styles.toast(type)}>
      {type === 'error' ? '✕ ' : '✓ '}{message}
    </div>
  );
}

// ============================================================
// COMPONENTE: Modal
// ============================================================
function Modal({ title, children, onClose }) {
  return (
    <div style={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={styles.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={styles.modalTitle}>{title}</div>
          <button 
            onClick={onClose}
            style={{ ...styles.btnSmall, background: 'transparent', color: colors.textMuted, fontSize: '20px' }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ============================================================
// COMPONENTE: ConfirmDialog
// ============================================================
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={{ ...styles.modal, maxWidth: '420px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: '36px', marginBottom: '16px' }}>⚠️</div>
        <p style={{ fontSize: '15px', color: colors.text, lineHeight: 1.6, marginBottom: '24px' }}>{message}</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button style={styles.btnSecondary} onClick={onCancel}>{t("common.cancel")}</button>
          <button style={styles.btnPrimary} onClick={onConfirm}>{t("common.confirm")}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// PAGE: DASHBOARD ADMIN
// ============================================================
function AdminDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      const data = await fetchApi('/dashboard');
      setMetrics(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={styles.loading}>{t("ad.loadingMetrics")}</div>;
  if (!metrics) return <div style={styles.emptyState}>{t("ad.dashError")}</div>;

  const { tenants, mrr, popularPlan, recentSignups, latestTenants, totalUsers } = metrics;

  return (
    <div>
      {/* Metric Cards */}
      <div style={styles.metricGrid}>
        <div style={styles.metricCard(colors.green)}>
          <div style={{ ...styles.metricValue, color: colors.green }}>{tenants.active}</div>
          <div style={styles.metricLabel}>{t("ad.activeTenants")}</div>
        </div>
        <div style={styles.metricCard(colors.gold)}>
          <div style={{ ...styles.metricValue, color: colors.gold }}>
            R$ {mrr.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
          </div>
          <div style={styles.metricLabel}>MRR</div>
        </div>
        <div style={styles.metricCard(colors.blue)}>
          <div style={{ ...styles.metricValue, color: colors.blue }}>{totalUsers}</div>
          <div style={styles.metricLabel}>{t("ad.totalUsers")}</div>
        </div>
        <div style={styles.metricCard(colors.purple)}>
          <div style={{ ...styles.metricValue, color: colors.purple }}>{recentSignups}</div>
          <div style={styles.metricLabel}>{t("ad.new30")}</div>
        </div>
      </div>

      {/* Status breakdown + Popular plan */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>{t("ad.tenantsStatus")}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { label: 'Ativos', value: tenants.active, color: colors.green },
              { label: 'Trial', value: tenants.trial, color: colors.blue },
              { label: 'Inadimplentes', value: tenants.overdue || 0, color: colors.orange },
              { label: 'Suspensos', value: tenants.suspended, color: colors.yellow },
              { label: 'Cancelados', value: tenants.cancelled, color: colors.red },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: colors.textMuted, fontSize: '14px' }}>{item.label}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: `${Math.max((item.value / Math.max(tenants.total, 1)) * 120, 4)}px`,
                    height: '6px',
                    borderRadius: '3px',
                    background: item.color,
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: 600, color: item.color, minWidth: '28px', textAlign: 'right' }}>
                    {item.value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>{t("sl.summary")}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>{t("ad.totalTenants")}</div>
              <div style={{ fontSize: '24px', fontWeight: 700 }}>{tenants.total}</div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>{t("ad.popularPlan")}</div>
              <div style={{ fontSize: '18px', fontWeight: 600, color: colors.gold }}>
                {popularPlan.name} <span style={{ fontSize: '13px', color: colors.textMuted }}>({popularPlan.count} tenants)</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '4px' }}>{t("ad.avgMrr")}</div>
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                R$ {tenants.active > 0 ? (mrr / tenants.active).toFixed(2) : '0.00'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Latest tenants */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>{t("ad.recentTenants")}</div>
        {latestTenants.length === 0 ? (
          <div style={styles.emptyState}>{t("ad.noTenants")}</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{t("cl.name")}</th>
                <th style={styles.th}>{t("ad.plan")}</th>
                <th style={styles.th}>{t("sl.status")}</th>
                <th style={styles.th}>{t("ad.createdAt")}</th>
              </tr>
            </thead>
            <tbody>
              {latestTenants.map(t => (
                <tr key={t.id}>
                  <td style={styles.td}>{t.name}</td>
                  <td style={styles.td}>{t.plan_name || '—'}</td>
                  <td style={styles.td}>
                    <span style={styles.statusBadge(t.status)}>{t.status}</span>
                  </td>
                  <td style={styles.td}>{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ============================================================
// PAGE: GESTÃO DE TENANTS
// ============================================================
function TenantsPage({ onShowToast }) {
  const [tenants, setTenants] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false); // 'create' | 'edit' | 'view' | false
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [confirm, setConfirm] = useState(null); // { message, action }
  const [form, setForm] = useState({
    name: '', email: '', phone: '', document: '', plan_id: '',
    master_name: '', master_email: '', master_password: '', modules: { atacado: false, fiado: false },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tenantsData, plansData] = await Promise.all([
        fetchApi('/tenants'),
        fetchApi('/plans'),
      ]);
      setTenants(tenantsData || []);
      setPlans(plansData || []);
    } catch (err) {
      onShowToast('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteTenant = async () => {
    if (!deletePassword) return;
    setDeleting(true);
    try {
      const r = await fetchApi('/tenants/' + deleteTarget.id + '/delete', { method: 'POST', body: JSON.stringify({ password: deletePassword }) });
      if (r) { onShowToast && onShowToast('Tenant apagado permanentemente', 'success'); setDeleteTarget(null); setDeletePassword(''); loadTenants(); }
    } catch(e) { alert(e.message || 'Erro ao apagar'); }
    finally { setDeleting(false); }
  };
  const loadTenants = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      const data = await fetchApi(`/tenants?${params}`);
      setTenants(data || []);
    } catch (err) {
      onShowToast('Erro ao filtrar', 'error');
    }
  };

  useEffect(() => {
    if (!loading) loadTenants();
  }, [search, filterStatus]);

  const resetForm = () => {
    setForm({ name: '', email: '', phone: '', document: '', plan_id: '', master_name: '', master_email: '', master_password: '', modules: { atacado: false, fiado: false } });
  };

  const handleCreate = async () => {
    try {
      await fetchApi('/tenants', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      onShowToast('Tenant criado com sucesso!', 'success');
      setShowModal(false);
      resetForm();
      loadTenants();
    } catch (err) {
      onShowToast(err.message, 'error');
    }
  };

  const handleUpdate = async () => {
    try {
      await fetchApi(`/tenants/${selectedTenant.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          document: form.document,
          plan_id: form.plan_id || null,
        }),
      });
      onShowToast('Tenant atualizado!', 'success');
      setShowModal(false);
      loadTenants();
    } catch (err) {
      onShowToast(err.message, 'error');
    }
  };

  const handleStatusChange = async (tenantId, newStatus) => {
    const statusLabels = { active: 'ativar', suspended: 'suspender', cancelled: 'cancelar' };
    setConfirm({
      message: `Tem certeza que deseja ${statusLabels[newStatus]} este tenant? ${newStatus === 'suspended' ? 'Todos os usuários serão desativados.' : ''}`,
      action: async () => {
        try {
          await fetchApi(`/tenants/${tenantId}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status: newStatus }),
          });
          onShowToast(`Status alterado para ${newStatus}`, 'success');
          loadTenants();
        } catch (err) {
          onShowToast(err.message, 'error');
        }
        setConfirm(null);
      },
    });
  };

  const [modulesTarget, setModulesTarget] = useState(null);
  const saveModules = async () => {
    try {
      await fetchApi(`/tenants/${modulesTarget.id}/modules`, { method: 'PATCH', body: JSON.stringify(modulesTarget.modules) });
      onShowToast('Módulos atualizados!', 'success');
      setModulesTarget(null);
      loadTenants();
    } catch (err) { onShowToast(err.message, 'error'); }
  };
  const openEdit = (tenant) => {
    setSelectedTenant(tenant);
    setForm({
      name: tenant.name,
      email: tenant.email,
      phone: tenant.phone || '',
      document: tenant.document || '',
      plan_id: tenant.plan_id || '',
      master_name: '', master_email: '', master_password: '', modules: { atacado: false, fiado: false },
    });
    setShowModal('edit');
  };

  const openView = async (tenant) => {
    try {
      const data = await fetchApi(`/tenants/${tenant.id}`);
      setSelectedTenant(data);
      setShowModal('view');
    } catch (err) {
      onShowToast('Erro ao carregar detalhes', 'error');
    }
  };

  if (loading) return <div style={styles.loading}>{t("ad.loadingTenants")}</div>;

  return (
    <div>
      {modulesTarget && (
        <Modal title={'Módulos — ' + modulesTarget.name} onClose={() => setModulesTarget(null)}>
          <div style={styles.formGroup}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 15, marginBottom: 12 }}>
              <input type="checkbox" checked={modulesTarget.modules.atacado} onChange={e => setModulesTarget({ ...modulesTarget, modules: { ...modulesTarget.modules, atacado: e.target.checked } })} />
              Atacado (preços e modo atacado)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 15 }}>
              <input type="checkbox" checked={modulesTarget.modules.fiado} onChange={e => setModulesTarget({ ...modulesTarget, modules: { ...modulesTarget.modules, fiado: e.target.checked } })} />
              Fiado (venda a prazo e crédito)
            </label>
          </div>
          <div style={styles.modalActions}>
            <button style={styles.btnSecondary} onClick={() => setModulesTarget(null)}>{t("common.cancel")}</button>
            <button style={styles.btnPrimary} onClick={saveModules}>{t("common.save")}</button>
          </div>
        </Modal>
      )}
      {/* Search & Filters */}
      <div style={styles.searchBar}>
        <input
          type="text"
          placeholder={t("ad.searchPh")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, maxWidth: '320px' }}
        />
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ ...styles.select, maxWidth: '180px' }}
        >
          <option value="">{t("ad.allStatus")}</option>
          <option value="active">{t("ad.actives")}</option>
          <option value="trial">Trial</option>
          <option value="suspended">{t("ad.suspended")}</option>
          <option value="cancelled">{t("ad.canceled")}</option>
        </select>
        <div style={{ flex: 1 }} />
        <button
          style={styles.btnPrimary}
          onClick={() => { resetForm(); setShowModal('create'); }}
        >
          + Novo Tenant
        </button>
      </div>

      {/* Table */}
      <div style={styles.card}>
        {tenants.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏪</div>
            <p>{t("ad.noTenantsFound")}</p>
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>{t("cl.name")}</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>{t("ad.plan")}</th>
                <th style={styles.th}>{t("ad.users")}</th>
                <th style={styles.th}>{t("sl.status")}</th>
                <th style={styles.th}>{t("ad.lastLogin")}</th>
                <th style={styles.th}>{t("ad.createdAt")}</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>{t("cl.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} style={styles.trHover}
                  onMouseEnter={e => e.currentTarget.style.background = colors.bgCardHover}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ ...styles.td, fontWeight: 600 }}>{t.name}</td>
                  <td style={{ ...styles.td, color: colors.textMuted }}>{t.email}</td>
                  <td style={styles.td}>
                    <span style={{ color: colors.gold }}>{t.plan_name || '—'}</span>
                    {t.plan_price && (
                      <span style={{ color: colors.textDim, fontSize: '12px', marginLeft: '6px' }}>
                        R${parseFloat(t.plan_price).toFixed(0)}
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>{t.user_count || 0}</td>
                  <td style={styles.td}>
                    <span style={styles.statusBadge(t.status)}>{t.status}</span>
                  </td>
                  <td style={{ ...styles.td, color: colors.textMuted, fontSize: '13px' }}>
                    {t.last_login_at ? new Date(t.last_login_at).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td style={{ ...styles.td, color: colors.textMuted }}>
                    {new Date(t.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                      <button
                        style={{ ...styles.btnSmall, background: `${colors.blue}20`, color: colors.blue }}
                        onClick={() => openView(t)}
                        title="Ver detalhes"
                      >
                        👁
                      </button>
                      <button
                        style={{ ...styles.btnSmall, background: `${colors.gold}20`, color: colors.gold }}
                        onClick={() => openEdit(t)}
                        title="Editar"
                      >
                        ✏️
                      </button>
                      {!t.is_admin_tenant && t.status === 'active' && (
                        <button
                          style={{ ...styles.btnSmall, background: `${colors.yellow}20`, color: colors.yellow }}
                          onClick={() => handleStatusChange(t.id, 'suspended')}
                          title="Suspender"
                        >
                          ⏸
                        </button>
                      )}
                      {!t.is_admin_tenant && (t.status === 'suspended' || t.status === 'cancelled') && (
                        <button
                          style={{ ...styles.btnSmall, background: `${colors.green}20`, color: colors.green }}
                          onClick={() => handleStatusChange(t.id, 'active')}
                          title="Reativar"
                        >
                          ▶
                        </button>
                      )}
                      {!t.is_admin_tenant && t.status !== 'cancelled' && (
                        <button
                          style={{ ...styles.btnSmall, background: `${colors.red}15`, color: colors.red }}
                          onClick={() => handleStatusChange(t.id, 'cancelled')}
                          title="Cancelar"
                        >
                          ✕
                        </button>
                      )}
                      <button
                        style={{ ...styles.btnSmall, background: '#2563EB20', color: '#3B82F6' }}
                        onClick={() => setModulesTarget({ id: t.id, name: t.name, modules: { atacado: !!(t.modules && t.modules.atacado), fiado: !!(t.modules && t.modules.fiado) } })}
                        title="Módulos (Atacado/Fiado)"
                      >
                        🧩
                      </button>
                      {!t.is_admin_tenant && (
                      <button
                        style={{ ...styles.btnSmall, background: '#4A151520', color: '#FF4444' }}
                        onClick={() => setDeleteTarget(t)}
                        title="Apagar permanentemente"
                      >
                        🗑
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

          {deleteTarget && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }} onClick={() => { setDeleteTarget(null); setDeletePassword(''); }}>
              <div onClick={e => e.stopPropagation()} style={{ background: '#1E293B', borderRadius: 16, padding: 28, width: 420, border: '1px solid #334155' }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: '#EF4444', marginBottom: 8 }}>{t("ad.deletePerm")}</div>
                <div style={{ fontSize: 14, color: '#94A3B8', marginBottom: 16 }}>{t("ad.allDataOf")} <strong style={{ color: '#F1F5F9' }}>{deleteTarget.name}</strong> serão apagados para sempre. Esta ação NÃO pode ser desfeita.</div>
                <div style={{ fontSize: 13, color: '#64748B', marginBottom: 6 }}>{t("ad.adminPassPrompt")}</div>
                <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && deleteTenant()} placeholder={t("ad.adminPassPh")} style={{ width: '100%', padding: '10px 14px', background: '#0F172A', border: '1px solid #334155', borderRadius: 8, color: '#F1F5F9', fontSize: 14, marginBottom: 16, outline: 'none', boxSizing: 'border-box' }} autoFocus />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={deleteTenant} disabled={deleting || !deletePassword} style={{ flex: 1, background: '#EF4444', border: 'none', borderRadius: 8, padding: '10px', color: '#FFF', fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: deleting || !deletePassword ? 0.5 : 1 }}>{deleting ? 'Apagando...' : 'Apagar Tudo'}</button>
                  <button onClick={() => { setDeleteTarget(null); setDeletePassword(''); }} style={{ flex: 1, background: '#334155', border: 'none', borderRadius: 8, padding: '10px', color: '#94A3B8', fontSize: 14, cursor: 'pointer' }}>{t("common.cancel")}</button>
                </div>
              </div>
            </div>
          )}
      {/* Create Modal (Onboarding) */}
      {showModal === 'create' && (
        <Modal title="Novo Tenant — Onboarding" onClose={() => setShowModal(false)}>
          <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '20px', padding: '12px', background: colors.bgInput, borderRadius: '8px' }}>
            Cria o tenant + usuário master em um único passo. O master receberá acesso imediato ao Aurora.
          </div>

          <div style={{ fontSize: '14px', fontWeight: 600, color: colors.gold, marginBottom: '12px' }}>{t("cf.tabCompany")}</div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("cf.companyName")}</label>
              <input style={styles.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={t("tf.companyPh")} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.companyEmail")}</label>
              <input style={styles.input} value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="contato@empresa.com" />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("cl.phone")}</label>
              <input style={styles.input} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="(11) 99999-9999" />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>CNPJ/CPF</label>
              <input style={styles.input} value={form.document} onChange={e => setForm({...form, document: e.target.value})} placeholder="00.000.000/0001-00" />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("ad.planReq")}</label>
            <select style={styles.select} value={form.plan_id} onChange={e => setForm({...form, plan_id: e.target.value})}>
              <option value="">{t("ad.selectPlan")}</option>
              {plans.filter(p => p.active).map(p => (
                <option key={p.id} value={p.id}>{p.name} — R$ {parseFloat(p.price).toFixed(2)}</option>
              ))}
            </select>
          </div>

          <div style={{ fontSize: '14px', fontWeight: 600, color: colors.gold, margin: '20px 0 12px' }}>{t("ad.masterUser")}</div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.masterName")}</label>
              <input style={styles.input} value={form.master_name} onChange={e => setForm({...form, master_name: e.target.value})} placeholder={t("tf.fullNamePh")} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.masterEmail")}</label>
              <input style={styles.input} value={form.master_email} onChange={e => setForm({...form, master_email: e.target.value})} placeholder="master@empresa.com" />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("ad.masterPass")}</label>
            <input style={styles.input} type="password" value={form.master_password} onChange={e => setForm({...form, master_password: e.target.value})} placeholder={t("tf.passwordPh")} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("ad.modulesEnabled")}</label>
            <div style={{ display: 'flex', gap: 20, marginTop: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input type="checkbox" checked={!!(form.modules && form.modules.atacado)} onChange={e => setForm({ ...form, modules: { ...(form.modules || {}), atacado: e.target.checked } })} />
                Atacado
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input type="checkbox" checked={!!(form.modules && form.modules.fiado)} onChange={e => setForm({ ...form, modules: { ...(form.modules || {}), fiado: e.target.checked } })} />
                Fiado (venda a prazo)
              </label>
            </div>
          </div>

          <div style={styles.modalActions}>
            <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>{t("common.cancel")}</button>
            <button 
              style={{
                ...styles.btnPrimary,
                opacity: (form.name && form.email && form.plan_id && form.master_name && form.master_email && form.master_password) ? 1 : 0.5,
              }}
              onClick={handleCreate}
              disabled={!(form.name && form.email && form.plan_id && form.master_name && form.master_email && form.master_password)}
            >
              Criar Tenant + Master
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Modal */}
      {showModal === 'edit' && selectedTenant && (
        <Modal title={`Editar: ${selectedTenant.name}`} onClose={() => setShowModal(false)}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("cl.name")}</label>
              <input style={styles.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input style={styles.input} value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("cl.phone")}</label>
              <input style={styles.input} value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>CNPJ/CPF</label>
              <input style={styles.input} value={form.document} onChange={e => setForm({...form, document: e.target.value})} />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("ad.plan")}</label>
            <select style={styles.select} value={form.plan_id} onChange={e => setForm({...form, plan_id: e.target.value})}>
              <option value="">{t("ad.noPlan")}</option>
              {plans.filter(p => p.active).map(p => (
                <option key={p.id} value={p.id}>{p.name} — R$ {parseFloat(p.price).toFixed(2)}</option>
              ))}
            </select>
          </div>

          <div style={styles.modalActions}>
            <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>{t("common.cancel")}</button>
            <button style={styles.btnPrimary} onClick={handleUpdate}>{t("sl.saveChanges")}</button>
          </div>
        </Modal>
      )}

      {/* View Modal */}
      {showModal === 'view' && selectedTenant && (
        <Modal title={selectedTenant.name} onClose={() => setShowModal(false)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <div style={styles.label}>Email</div>
              <div style={{ fontSize: '14px' }}>{selectedTenant.email}</div>
            </div>
            <div>
              <div style={styles.label}>{t("cl.phone")}</div>
              <div style={{ fontSize: '14px' }}>{selectedTenant.phone || '—'}</div>
            </div>
            <div>
              <div style={styles.label}>{t("ad.document")}</div>
              <div style={{ fontSize: '14px' }}>{selectedTenant.document || '—'}</div>
            </div>
            <div>
              <div style={styles.label}>{t("sl.status")}</div>
              <span style={styles.statusBadge(selectedTenant.status)}>{selectedTenant.status}</span>
            </div>
            <div>
              <div style={styles.label}>{t("ad.plan")}</div>
              <div style={{ fontSize: '14px', color: colors.gold }}>{selectedTenant.plan_name || '—'}</div>
            </div>
            <div>
              <div style={styles.label}>{t("ad.createdAt")}</div>
              <div style={{ fontSize: '14px' }}>{new Date(selectedTenant.created_at).toLocaleDateString('pt-BR')}</div>
            </div>
          </div>

          {selectedTenant.users && selectedTenant.users.length > 0 && (
            <>
              <div style={{ ...styles.cardTitle, marginTop: '8px' }}>Usuários ({selectedTenant.users.length})</div>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>{t("cl.name")}</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Role</th>
                    <th style={styles.th}>{t("cf.active")}</th>
                    <th style={styles.th}>{t("cl.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTenant.users.map(u => (
                    <tr key={u.id}>
                      <td style={styles.td}>{u.name}</td>
                      <td style={{ ...styles.td, color: colors.textMuted }}>{u.email}</td>
                      <td style={styles.td}>
                        <span style={{
                          color: u.role === 'master' ? colors.gold : colors.blue,
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}>{u.role}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ color: u.active ? colors.green : colors.red }}>
                          {u.active ? '●' : '○'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {u.role !== 'admin' && (
                          <span
                            onClick={async () => {
                              const np = window.prompt('Nova senha para ' + u.name + ' (deixe VAZIO para gerar automática):');
                              if (np === null) return;
                              try {
                                const body = np.trim() ? { password: np.trim() } : {};
                                const r = await fetchApi('/users/' + u.id + '/reset-password', { method: 'PATCH', body: JSON.stringify(body) });
                                if (r.temp_password) {
                                  window.prompt('Senha temporária de ' + u.name + ' — copie agora, não será exibida novamente:', r.temp_password);
                                }
                                onShowToast('Senha resetada. O usuário deverá trocá-la no próximo login.', 'success');
                              } catch (e) { onShowToast(e.message || 'Erro ao resetar senha', 'error'); }
                            }}
                            style={{ fontSize: '13px', cursor: 'pointer', color: colors.blue }}
                          >{t("ad.resetPass")}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Modal>
      )}

      {/* Confirm Dialog */}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.action}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// PAGE: GESTÃO DE PLANOS
// ============================================================
function PlansPage({ onShowToast }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false); // 'create' | 'edit' | false
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({
    name: '', price: '', max_users: '5', max_storage_mb: '1024', modules: [], description: '',
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const data = await fetchApi('/plans');
      setPlans(data || []);
    } catch (err) {
      onShowToast('Erro ao carregar planos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ name: '', price: '', max_users: '5', max_storage_mb: '1024', modules: [], description: '' });
  };

  const handleCreate = async () => {
    try {
      await fetchApi('/plans', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price),
          max_users: parseInt(form.max_users),
          max_storage_mb: parseInt(form.max_storage_mb),
        }),
      });
      onShowToast('Plano criado!', 'success');
      setShowModal(false);
      resetForm();
      loadPlans();
    } catch (err) {
      onShowToast(err.message, 'error');
    }
  };

  const handleUpdate = async () => {
    try {
      await fetchApi(`/plans/${selectedPlan.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...form,
          price: parseFloat(form.price),
          max_users: parseInt(form.max_users),
          max_storage_mb: parseInt(form.max_storage_mb),
        }),
      });
      onShowToast('Plano atualizado!', 'success');
      setShowModal(false);
      loadPlans();
    } catch (err) {
      onShowToast(err.message, 'error');
    }
  };

  const handleDeactivate = (plan) => {
    setConfirm({
      message: `Desativar o plano "${plan.name}"? ${plan.tenant_count > 0 ? `Atenção: ${plan.tenant_count} tenant(s) ativo(s) neste plano — migre-os primeiro.` : ''}`,
      action: async () => {
        try {
          await fetchApi(`/plans/${plan.id}`, { method: 'DELETE' });
          onShowToast('Plano desativado', 'success');
          loadPlans();
        } catch (err) {
          onShowToast(err.message, 'error');
        }
        setConfirm(null);
      },
    });
  };

  const openEdit = (plan) => {
    setSelectedPlan(plan);
    const modules = typeof plan.modules === 'string' ? JSON.parse(plan.modules) : (plan.modules || []);
    setForm({
      name: plan.name,
      price: plan.price.toString(),
      max_users: plan.max_users.toString(),
      max_storage_mb: plan.max_storage_mb.toString(),
      modules,
      description: plan.description || '',
    });
    setShowModal('edit');
  };

  const toggleModule = (key) => {
    setForm(prev => ({
      ...prev,
      modules: prev.modules.includes(key)
        ? prev.modules.filter(m => m !== key)
        : [...prev.modules, key],
    }));
  };

  if (loading) return <div style={styles.loading}>{t("ad.loadingPlans")}</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button style={styles.btnPrimary} onClick={() => { resetForm(); setShowModal('create'); }}>
          + Novo Plano
        </button>
      </div>

      {/* Plans Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
        {plans.map(plan => {
          const modules = typeof plan.modules === 'string' ? JSON.parse(plan.modules) : (plan.modules || []);
          return (
            <div key={plan.id} style={{
              ...styles.card,
              opacity: plan.active ? 1 : 0.5,
              borderColor: plan.active ? colors.border : `${colors.red}44`,
              position: 'relative',
            }}>
              {!plan.active && (
                <div style={{
                  position: 'absolute', top: '12px', right: '12px',
                  background: `${colors.red}20`, color: colors.red,
                  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                }}>
                  INATIVO
                </div>
              )}
              <div style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>{plan.name}</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: colors.gold, marginBottom: '4px' }}>
                R$ {parseFloat(plan.price).toFixed(2)}
                <span style={{ fontSize: '14px', color: colors.textMuted, fontWeight: 400 }}>/mês</span>
              </div>
              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '16px' }}>
                {plan.description}
              </div>

              <div style={{ fontSize: '12px', color: colors.textDim, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Limites
              </div>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <span style={{ color: colors.blue, fontWeight: 600 }}>{plan.max_users}</span>
                  <span style={{ color: colors.textMuted, fontSize: '13px' }}> usuários</span>
                </div>
                <div>
                  <span style={{ color: colors.purple, fontWeight: 600 }}>{plan.max_storage_mb >= 1024 ? `${(plan.max_storage_mb / 1024).toFixed(0)}GB` : `${plan.max_storage_mb}MB`}</span>
                  <span style={{ color: colors.textMuted, fontSize: '13px' }}> storage</span>
                </div>
              </div>

              <div style={{ fontSize: '12px', color: colors.textDim, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Módulos ({modules.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '16px' }}>
                {modules.map(m => (
                  <span key={m} style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                    background: `${colors.gold}15`, color: colors.gold,
                  }}>{m}</span>
                ))}
              </div>

              <div style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '16px' }}>
                <span style={{ color: colors.green, fontWeight: 600 }}>{plan.tenant_count || 0}</span> tenants ativos
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{ ...styles.btnSmall, background: `${colors.gold}20`, color: colors.gold, flex: 1 }}
                  onClick={() => openEdit(plan)}
                >
                  Editar
                </button>
                {plan.active && (
                  <button
                    style={{ ...styles.btnSmall, background: `${colors.red}15`, color: colors.red }}
                    onClick={() => handleDeactivate(plan)}
                  >
                    Desativar
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <Modal title={showModal === 'create' ? 'Novo Plano' : `Editar: ${selectedPlan.name}`} onClose={() => setShowModal(false)}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.planName")}</label>
              <input style={styles.input} value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={t("ad.planNamePh")} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.monthlyPrice")}</label>
              <input style={styles.input} type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="197.00" />
            </div>
          </div>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.maxUsers")}</label>
              <input style={styles.input} type="number" value={form.max_users} onChange={e => setForm({...form, max_users: e.target.value})} />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>{t("ad.storage")}</label>
              <input style={styles.input} type="number" value={form.max_storage_mb} onChange={e => setForm({...form, max_storage_mb: e.target.value})} />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>{t("os.desc")}</label>
            <input style={styles.input} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder={t("ad.planDescPh")} />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>{t("ad.modulesIncluded")}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' }}>
              {ALL_MODULES.map(mod => (
                <label key={mod.key} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '8px 12px', borderRadius: '8px', cursor: 'pointer',
                  background: form.modules.includes(mod.key) ? `${colors.gold}15` : colors.bgInput,
                  border: `1px solid ${form.modules.includes(mod.key) ? colors.gold + '44' : colors.border}`,
                  transition: 'all 0.15s',
                }}>
                  <input
                    type="checkbox"
                    checked={form.modules.includes(mod.key)}
                    onChange={() => toggleModule(mod.key)}
                    style={{ accentColor: colors.gold }}
                  />
                  <span style={{
                    fontSize: '13px',
                    color: form.modules.includes(mod.key) ? colors.gold : colors.textMuted,
                  }}>{mod.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div style={styles.modalActions}>
            <button style={styles.btnSecondary} onClick={() => setShowModal(false)}>{t("common.cancel")}</button>
            <button
              style={{ ...styles.btnPrimary, opacity: (form.name && form.price) ? 1 : 0.5 }}
              onClick={showModal === 'create' ? handleCreate : handleUpdate}
              disabled={!(form.name && form.price)}
            >
              {showModal === 'create' ? 'Criar Plano' : 'Salvar Alterações'}
            </button>
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.action}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// APP PRINCIPAL: ADMIN PANEL
// ============================================================

// ============================================================
// ADMIN TICKETS PAGE
// ============================================================
function AdminTicketsPage({ onShowToast }) {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);

  const loadTickets = useCallback(async () => {
    const params = filter !== "all" ? "?status=" + filter : "";
    const data = await fetchApi("/tickets" + params);
    if (data) setTickets(data);
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadTickets(); setSelected(null); setDetail(null); }, [loadTickets]);

  const loadDetail = async (id) => {
    const data = await fetchApi("/tickets/" + id);
    if (data) { setDetail(data); setSelected(id); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    const data = await fetchApi("/tickets/" + selected + "/messages", { method: "POST", body: JSON.stringify({ message: reply }) });
    if (data) { setReply(""); loadDetail(selected); onShowToast && onShowToast("Resposta enviada", "success"); }
  };

  const closeTicket = async (id) => {
    if (!window.confirm(t("ad.closeTicketConfirm"))) return;
    const data = await fetchApi("/tickets/" + (id || selected) + "/close", { method: "PATCH" });
    if (data) { onShowToast && onShowToast("Chamado fechado", "success"); loadTickets(); if (selected) loadDetail(selected); }
  };

  const fmtDate = (d) => { if (!d) return ""; const dt = new Date(d); return dt.toLocaleDateString("pt-BR") + " " + dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); };
  const statusColors = { aberto: "#F59E0B", em_atendimento: "#3B82F6", fechado: "#6B7280" };
  const statusLabels = { aberto: "Aberto", em_atendimento: "Em Atendimento", fechado: "Fechado" };
  const priorityColors = { baixa: "#10B981", normal: "#F59E0B", alta: "#EF4444", urgente: "#DC2626" };

  if (selected && detail) return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#F1F5F9" }}>#{detail.id} — {detail.subject}</div>
          <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
            <span style={{ fontSize: 12, color: statusColors[detail.status], fontWeight: 500 }}>● {statusLabels[detail.status]}</span>
            <span style={{ fontSize: 12, color: priorityColors[detail.priority], textTransform: "capitalize" }}>{detail.priority}</span>
            <span style={{ fontSize: 12, color: "#94A3B8" }}>Tenant: {detail.tenant_name}</span>
            <span style={{ fontSize: 12, color: "#94A3B8" }}>Por: {detail.user_name}</span>
            <span style={{ fontSize: 12, color: "#64748B" }}>{fmtDate(detail.created_at)}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {detail.status !== "fechado" && <button onClick={() => closeTicket()} style={{ background: "#EF4444", border: "none", borderRadius: 8, padding: "8px 16px", color: "#FFF", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>{t("ad.closeTicket")}</button>}
          <button onClick={() => { setSelected(null); setDetail(null); }} style={{ background: "#334155", border: "none", borderRadius: 8, padding: "8px 16px", color: "#CBD5E1", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>← Voltar</button>
        </div>
      </div>
      <div style={{ background: "#1E293B", borderRadius: 10, padding: 20, marginBottom: 16, maxHeight: 400, overflowY: "auto" }}>
        {detail.messages && detail.messages.map(m => (
          <div key={m.id} style={{ marginBottom: 12, display: "flex", flexDirection: "column", alignItems: m.sender_type === "admin" ? "flex-end" : "flex-start" }}>
            <div style={{ background: m.sender_type === "admin" ? "#1E3A5F" : "#334155", borderRadius: 10, padding: "10px 14px", maxWidth: "75%", wordBreak: "break-word" }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: m.sender_type === "admin" ? "#60A5FA" : "#F59E0B", marginBottom: 4 }}>{m.sender_name || m.sender_type}</div>
              <div style={{ fontSize: 14, color: "#F1F5F9", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.message}</div>
              <div style={{ fontSize: 10, color: "#64748B", marginTop: 4, textAlign: "right" }}>{fmtDate(m.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
      {detail.status !== "fechado" ? (
        <div style={{ display: "flex", gap: 8 }}>
          <textarea value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }} placeholder={t("ad.replyPh")} rows={2} style={{ flex: 1, padding: "10px 12px", background: "#1E293B", border: "1px solid #334155", borderRadius: 8, color: "#F1F5F9", fontSize: 14, fontFamily: "inherit", resize: "none", outline: "none" }} />
          <button onClick={sendReply} disabled={!reply.trim()} style={{ background: "#F59E0B", border: "none", borderRadius: 8, padding: "8px 18px", color: "#FFF", fontWeight: 500, fontSize: 14, cursor: "pointer", alignSelf: "flex-end", opacity: reply.trim() ? 1 : 0.5 }}>{t("tk.send")}</button>
        </div>
      ) : (
        <div style={{ background: "#1E293B", borderRadius: 10, padding: 16, textAlign: "center" }}>
          <span style={{ color: "#64748B", fontSize: 14 }}>Chamado fechado {detail.rating ? "— Avaliação: " + "★".repeat(detail.rating) + "☆".repeat(5 - detail.rating) : "— Sem avaliação"}</span>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#F1F5F9" }}>{t("ad.supportTickets")}</div>
        <div style={{ display: "flex", gap: 8 }}>
          {[["all","Todos"],["aberto","Abertos"],["em_atendimento","Em Atendimento"],["fechado","Fechados"]].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)} style={{ background: filter === k ? "#F59E0B" : "#334155", border: "none", borderRadius: 8, padding: "7px 14px", color: filter === k ? "#FFF" : "#94A3B8", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>{l}</button>
          ))}
        </div>
      </div>
      {loading ? <div style={{ color: "#94A3B8", textAlign: "center", padding: 40 }}>{t("common.loading")}</div> : tickets.length === 0 ? (
        <div style={{ color: "#64748B", textAlign: "center", padding: 40 }}>Nenhum chamado {filter !== "all" ? "com este filtro" : "registrado"}</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ borderBottom: "1px solid #334155" }}>
            {["#","Assunto","Tenant","Prioridade","Status","Msgs","Data","Ações"].map(h => <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 12, color: "#64748B", fontWeight: 500 }}>{h}</th>)}
          </tr></thead>
          <tbody>{tickets.map(t => (
            <tr key={t.id} style={{ borderBottom: "1px solid #1E293B", cursor: "pointer" }} onClick={() => loadDetail(t.id)}>
              <td style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8" }}>{t.id}</td>
              <td style={{ padding: "10px 12px", fontSize: 14, color: "#F1F5F9", fontWeight: 500 }}>{t.subject}</td>
              <td style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8" }}>{t.tenant_name}</td>
              <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 12, color: priorityColors[t.priority], textTransform: "capitalize", fontWeight: 500 }}>{t.priority}</span></td>
              <td style={{ padding: "10px 12px" }}><span style={{ fontSize: 12, color: statusColors[t.status], fontWeight: 500 }}>● {statusLabels[t.status]}</span></td>
              <td style={{ padding: "10px 12px", fontSize: 13, color: "#94A3B8" }}>{t.msg_count}</td>
              <td style={{ padding: "10px 12px", fontSize: 12, color: "#64748B" }}>{fmtDate(t.created_at)}</td>
              <td style={{ padding: "10px 12px" }}>{t.status !== "fechado" && <button onClick={(e) => { e.stopPropagation(); closeTicket(t.id); }} style={{ background: "#EF4444", border: "none", borderRadius: 6, padding: "5px 10px", color: "#FFF", fontSize: 11, cursor: "pointer" }}>{t("common.close")}</button>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}


// ============================================================
// SUBSCRIPTIONS PAGE (Admin)
// ============================================================
function SubscriptionsPage({ onShowToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activateModal, setActivateModal] = useState(null);
  const [activateForm, setActivateForm] = useState({ plan_id: '', months: 1, method: 'manual', notes: '' });
  const [plans, setPlans] = useState([]);

  const loadData = async () => {
    try {
      const token = window.__auroraToken;
      const [overviewRes, plansRes] = await Promise.all([
        fetch('/api/admin/subscriptions/overview', { headers: { Authorization: 'Bearer ' + token } }),
        fetch('/api/admin/plans', { headers: { Authorization: 'Bearer ' + token } })
      ]);
      const overview = await overviewRes.json();
      const plansData = await plansRes.json();
      setData(overview);
      setPlans(Array.isArray(plansData) ? plansData : []);
    } catch (e) { console.error('Load subs err:', e); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleActivate = async (tenantId) => {
    try {
      const token = window.__auroraToken;
      const r = await fetch('/api/admin/subscriptions/' + tenantId + '/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify(activateForm)
      });
      const d = await r.json();
      if (r.ok) {
        onShowToast && onShowToast('Assinatura ativada!', 'success');
        setActivateModal(null);
        setActivateForm({ plan_id: '', months: 1, method: 'manual', notes: '' });
        loadData();
      } else { onShowToast && onShowToast(d.error || 'Erro', 'error'); }
    } catch (e) { onShowToast && onShowToast('Erro ao ativar', 'error'); }
  };

  const handleRenew = async (tenantId) => {
    if (!window.confirm('Renovar assinatura por +1 mês?')) return;
    try {
      const token = window.__auroraToken;
      const r = await fetch('/api/admin/subscriptions/' + tenantId + '/renew', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ method: 'manual', months: 1, notes: 'Renovação manual admin' })
      });
      if (r.ok) { onShowToast && onShowToast('Renovado!', 'success'); loadData(); }
      else { const d = await r.json(); onShowToast && onShowToast(d.error || 'Erro', 'error'); }
    } catch (e) { onShowToast && onShowToast('Erro ao renovar', 'error'); }
  };

  const handleCancel = async (tenantId, name) => {
    if (!window.confirm('Cancelar assinatura de ' + name + '?')) return;
    try {
      const token = window.__auroraToken;
      const r = await fetch('/api/admin/subscriptions/' + tenantId + '/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ reason: 'Cancelado manualmente pelo admin' })
      });
      if (r.ok) { onShowToast && onShowToast('Cancelado', 'success'); loadData(); }
      else { const d = await r.json(); onShowToast && onShowToast(d.error || 'Erro', 'error'); }
    } catch (e) { onShowToast && onShowToast('Erro', 'error'); }
  };

  if (loading) return React.createElement('div', { style: { textAlign: 'center', padding: '60px', color: '#999' } }, 'Carregando assinaturas...');
  if (!data) return React.createElement('div', { style: { textAlign: 'center', padding: '60px', color: '#f66' } }, 'Erro ao carregar dados');

  const { tenants: subs, revenue } = data;
  const statusColor = { active: '#4CAF50', grace: '#FF9800', overdue: '#f44336', pending: '#2196F3', cancelled: '#999' };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
  const storagePercent = (used, max) => max ? Math.round((parseFloat(used || 0) / max) * 100) : 0;

  return React.createElement('div', null,
    // Revenue cards
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' } },
      React.createElement('div', { style: { background: '#1a1a2e', borderRadius: '12px', padding: '20px', border: '1px solid rgba(212,175,55,0.2)' } },
        React.createElement('div', { style: { color: '#999', fontSize: '13px', marginBottom: '4px' } }, 'MRR'),
        React.createElement('div', { style: { color: '#d4af37', fontSize: '28px', fontWeight: '700' } }, 'R$ ' + (revenue?.mrr || 0).toFixed(2))
      ),
      React.createElement('div', { style: { background: '#1a1a2e', borderRadius: '12px', padding: '20px', border: '1px solid rgba(76,175,80,0.2)' } },
        React.createElement('div', { style: { color: '#999', fontSize: '13px', marginBottom: '4px' } }, 'Assinaturas Ativas'),
        React.createElement('div', { style: { color: '#4CAF50', fontSize: '28px', fontWeight: '700' } }, revenue?.active_subscriptions || 0)
      ),
      React.createElement('div', { style: { background: '#1a1a2e', borderRadius: '12px', padding: '20px', border: '1px solid rgba(33,150,243,0.2)' } },
        React.createElement('div', { style: { color: '#999', fontSize: '13px', marginBottom: '4px' } }, 'Por Plano'),
        (revenue?.by_plan || []).map((p, i) => React.createElement('div', { key: i, style: { color: '#ccc', fontSize: '13px' } }, p.name + ': ' + p.count + ' (R$' + parseFloat(p.revenue).toFixed(0) + ')'))
      )
    ),
    // Tenants table
    React.createElement('div', { style: { background: '#1a1a2e', borderRadius: '12px', padding: '20px', border: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' } },
      React.createElement('h3', { style: { color: '#fff', margin: '0 0 16px', fontSize: '16px' } }, 'Assinaturas por Tenant'),
      React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse' } },
        React.createElement('thead', null,
          React.createElement('tr', null,
            ['Tenant', 'Plano', 'Sub Status', 'Expira', 'Dias', 'Método', 'Storage', 'Ações'].map((h, i) =>
              React.createElement('th', { key: i, style: { textAlign: 'left', padding: '10px 8px', color: '#999', fontSize: '12px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontWeight: '500' } }, h)
            )
          )
        ),
        React.createElement('tbody', null,
          (subs || []).map((t, i) => {
            const sp = storagePercent(t.storage_used_mb, t.max_storage_mb);
            return React.createElement('tr', { key: i, style: { borderBottom: '1px solid rgba(255,255,255,0.04)' } },
              React.createElement('td', { style: { padding: '10px 8px', color: '#fff', fontSize: '13px' } },
                React.createElement('div', null, t.name),
                React.createElement('div', { style: { color: '#666', fontSize: '11px' } }, t.email)
              ),
              React.createElement('td', { style: { padding: '10px 8px', color: '#d4af37', fontSize: '13px' } }, (t.plan_name || '—') + (t.plan_price ? ' R$' + parseFloat(t.plan_price).toFixed(0) : '')),
              React.createElement('td', { style: { padding: '10px 8px' } },
                React.createElement('span', { style: { background: statusColor[t.sub_status] || '#555', color: '#fff', padding: '2px 8px', borderRadius: '10px', fontSize: '11px' } }, t.sub_status || 'sem sub')
              ),
              React.createElement('td', { style: { padding: '10px 8px', color: '#ccc', fontSize: '13px' } }, formatDate(t.expires_at)),
              React.createElement('td', { style: { padding: '10px 8px', color: t.days_until_expiry < 0 ? '#f44' : t.days_until_expiry <= 7 ? '#FF9800' : '#4CAF50', fontSize: '13px', fontWeight: '600' } }, t.days_until_expiry != null ? t.days_until_expiry + 'd' : '—'),
              React.createElement('td', { style: { padding: '10px 8px', color: '#999', fontSize: '12px' } }, t.paid_method || '—'),
              React.createElement('td', { style: { padding: '10px 8px' } },
                React.createElement('div', { style: { background: '#333', borderRadius: '4px', height: '6px', width: '60px' } },
                  React.createElement('div', { style: { background: sp > 80 ? '#f44' : sp > 50 ? '#FF9800' : '#4CAF50', height: '6px', borderRadius: '4px', width: Math.min(sp, 100) + '%' } })
                ),
                React.createElement('div', { style: { color: '#666', fontSize: '10px', marginTop: '2px' } }, sp + '%')
              ),
              React.createElement('td', { style: { padding: '10px 8px' } },
                React.createElement('div', { style: { display: 'flex', gap: '4px' } },
                  React.createElement('button', {
                    onClick: () => { setActivateModal(t.id); setActivateForm({ ...activateForm, plan_id: t.plan_id || '' }); },
                    title: 'Ativar/Renovar',
                    style: { background: '#4CAF50', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }
                  }, '💳'),
                  t.sub_status === 'active' && React.createElement('button', {
                    onClick: () => handleRenew(t.id),
                    title: 'Renovar +1 mês',
                    style: { background: '#2196F3', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }
                  }, '🔄'),
                  t.sub_status && t.sub_status !== 'cancelled' && React.createElement('button', {
                    onClick: () => handleCancel(t.id, t.name),
                    title: 'Cancelar',
                    style: { background: '#f44336', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px' }
                  }, '✕')
                )
              )
            );
          })
        )
      )
    ),
    // Activate modal
    activateModal && React.createElement('div', {
      style: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
      onClick: (e) => { if (e.target === e.currentTarget) setActivateModal(null); }
    },
      React.createElement('div', { style: { background: '#1a1a2e', borderRadius: '16px', padding: '30px', width: '400px', border: '1px solid rgba(212,175,55,0.3)' } },
        React.createElement('h3', { style: { color: '#d4af37', margin: '0 0 20px' } }, 'Ativar Assinatura'),
        React.createElement('div', { style: { marginBottom: '12px' } },
          React.createElement('label', { style: { color: '#999', fontSize: '12px', display: 'block', marginBottom: '4px' } }, 'Plano'),
          React.createElement('select', {
            value: activateForm.plan_id,
            onChange: (e) => setActivateForm({ ...activateForm, plan_id: parseInt(e.target.value) }),
            style: { width: '100%', padding: '8px', background: '#0d0d1a', color: '#fff', border: '1px solid #333', borderRadius: '8px' }
          },
            React.createElement('option', { value: '' }, 'Selecione...'),
            plans.filter(p => p.price > 0).map(p => React.createElement('option', { key: p.id, value: p.id }, p.name + ' - R$' + parseFloat(p.price).toFixed(2)))
          )
        ),
        React.createElement('div', { style: { marginBottom: '12px' } },
          React.createElement('label', { style: { color: '#999', fontSize: '12px', display: 'block', marginBottom: '4px' } }, 'Meses'),
          React.createElement('input', {
            type: 'number', min: 1, max: 12, value: activateForm.months,
            onChange: (e) => setActivateForm({ ...activateForm, months: parseInt(e.target.value) || 1 }),
            style: { width: '100%', padding: '8px', background: '#0d0d1a', color: '#fff', border: '1px solid #333', borderRadius: '8px' }
          })
        ),
        React.createElement('div', { style: { marginBottom: '12px' } },
          React.createElement('label', { style: { color: '#999', fontSize: '12px', display: 'block', marginBottom: '4px' } }, 'Método'),
          React.createElement('select', {
            value: activateForm.method,
            onChange: (e) => setActivateForm({ ...activateForm, method: e.target.value }),
            style: { width: '100%', padding: '8px', background: '#0d0d1a', color: '#fff', border: '1px solid #333', borderRadius: '8px' }
          },
            React.createElement('option', { value: 'manual' }, 'Manual'),
            React.createElement('option', { value: 'pix' }, 'Pix'),
            React.createElement('option', { value: 'boleto' }, 'Boleto'),
            React.createElement('option', { value: 'cartao' }, 'Cartão'),
            React.createElement('option', { value: 'cortesia' }, 'Cortesia')
          )
        ),
        React.createElement('div', { style: { marginBottom: '16px' } },
          React.createElement('label', { style: { color: '#999', fontSize: '12px', display: 'block', marginBottom: '4px' } }, 'Observações'),
          React.createElement('input', {
            type: 'text', value: activateForm.notes, placeholder: 'Opcional',
            onChange: (e) => setActivateForm({ ...activateForm, notes: e.target.value }),
            style: { width: '100%', padding: '8px', background: '#0d0d1a', color: '#fff', border: '1px solid #333', borderRadius: '8px' }
          })
        ),
        React.createElement('div', { style: { display: 'flex', gap: '10px' } },
          React.createElement('button', {
            onClick: () => handleActivate(activateModal),
            disabled: !activateForm.plan_id,
            style: { flex: 1, padding: '10px', background: activateForm.plan_id ? '#4CAF50' : '#333', color: '#fff', border: 'none', borderRadius: '8px', cursor: activateForm.plan_id ? 'pointer' : 'default', fontWeight: '600' }
          }, 'Ativar'),
          React.createElement('button', {
            onClick: () => setActivateModal(null),
            style: { flex: 1, padding: '10px', background: '#333', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }
          }, 'Cancelar')
        )
      )
    )
  );
}

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);


  const tabs = [
    { key: 'dashboard', label: '📊 Dashboard', icon: '📊' },
    { key: 'tenants', label: '🏪 Tenants', icon: '🏪' },
    { key: 'plans', label: '📦 Planos', icon: '📦' },
    { key: 'tickets', label: '🎫 Chamados', icon: '🎫' },
    { key: 'subscriptions', label: '💳 Assinaturas', icon: '💳' },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={styles.headerTitle}>AURORA</div>
          <span style={styles.headerBadge}>{t("ad.panel")}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '13px', color: colors.textMuted }}>Neurotech-X</span>
          <button
            style={{ ...styles.btnSmall, background: `${colors.blue}15`, color: colors.blue }}
            onClick={async () => {
              const atual = window.prompt('Senha atual:');
              if (!atual) return;
              const nova = window.prompt('Nova senha (mínimo 6 caracteres):');
              if (!nova) return;
              const conf = window.prompt('Confirme a nova senha:');
              if (nova !== conf) return alert('As senhas não conferem');
              try {
                await fetchApi('/../auth/change-password', { method: 'POST', body: JSON.stringify({ current_password: atual, new_password: nova }) });
                alert('Senha alterada com sucesso!');
              } catch (e) { alert(e.message || 'Erro ao trocar senha'); }
            }}
          >
            {t("ad.changePw")}
          </button>
          <select
            value={getLang()}
            onChange={async (e) => {
              const l = e.target.value;
              try {
                await fetchApi('/../tenant', { method: 'PUT', body: JSON.stringify({ lang: l }) });
                setLang(l);
              } catch (err) { alert(err.message || 'Erro ao trocar idioma'); }
            }}
            style={{ ...styles.btnSmall, cursor: 'pointer' }}
          >
            <option value="pt">Português</option>
            <option value="es">Español</option>
            <option value="en">English</option>
          </select>
          <button
            style={{ ...styles.btnSmall, background: `${colors.red}15`, color: colors.red }}
            onClick={() => {
              
              window.location.href = '/login';
            }}
          >
            {t("ad.logout")}
          </button>
        </div>
      </div>

      {/* Nav Tabs */}
      <div style={styles.nav}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            style={styles.navTab(activeTab === tab.key)}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {activeTab === 'dashboard' && <AdminDashboard />}
        {activeTab === 'tenants' && <TenantsPage onShowToast={showToast} />}
        {activeTab === 'plans' && <PlansPage onShowToast={showToast} />}
        {activeTab === 'tickets' && <AdminTicketsPage onShowToast={showToast} />}
        {activeTab === "subscriptions" && <SubscriptionsPage onShowToast={showToast} />}
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
